import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Lock, Mail } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const u = await login(email, password);
      navigate(u.must_change_password ? "/cambia-password" : "/");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="relative hidden lg:block">
        <img
          src="https://images.pexels.com/photos/8761636/pexels-photo-8761636.jpeg"
          alt="Kiwanis"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-navy/85" />
        <div className="relative z-10 h-full flex flex-col justify-between p-12 text-white">
          <div className="mx-auto bg-white rounded-xl p-5 shadow-lg w-fit">
            <img src="/kiwanis-logo.jpeg" alt="Kiwanis Distretto Italia-San Marino" className="h-20 lg:h-24 w-auto object-contain" />
          </div>
          <div>
            <h1 className="font-display text-4xl sm:text-5xl font-bold leading-tight">Elenco Soci del Distretto</h1>
            <p className="mt-4 text-white/80 max-w-md text-base leading-relaxed">
              Directory riservata del Distretto Italia-San Marino. Consulta soci, club, divisioni ed eventi in un unico spazio istituzionale.
            </p>
            <div className="mt-6 h-1 w-24 bg-gold rounded-full" />
          </div>
          <p className="text-xs uppercase tracking-[0.15em] text-white/60">Accesso riservato ai soci</p>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12 bg-background">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex justify-center mb-8">
            <img src="/kiwanis-logo.jpeg" alt="Kiwanis Distretto Italia-San Marino" className="h-20 w-auto object-contain" />
          </div>
          <p className="text-xs tracking-[0.15em] uppercase font-bold text-gold">Area riservata</p>
          <h2 className="font-display text-3xl font-bold text-navy mt-2">Accedi al tuo account</h2>
          <p className="text-muted-foreground mt-2 text-sm">Inserisci le credenziali fornite dal Distretto.</p>

          <form onSubmit={submit} className="mt-8 space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                  placeholder="nome@example.com" className="pl-10 h-11" data-testid="login-email-input" />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••" className="pl-10 h-11" data-testid="login-password-input" />
              </div>
            </div>
            {error && <p className="text-sm text-destructive font-medium" data-testid="login-error">{error}</p>}
            <Button type="submit" disabled={loading} data-testid="login-submit-button"
              className="w-full h-11 bg-navy hover:bg-navy-hover text-white font-semibold">
              {loading ? "Accesso…" : "Accedi"}
            </Button>
            <div className="text-center">
              <a href="/password-dimenticata" data-testid="forgot-password-link"
                className="text-sm text-navy hover:text-gold font-medium transition-colors">
                Password dimenticata?
              </a>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
